/**
 * CLIENT-SAFE trust display constants + band/status TYPES.
 *
 * This file is safe to import from client code. It contains ONLY presentation
 * labels and type unions — NO thresholds, NO suppression rules, NO count/
 * fraction mapping, NO privacy logic. Those live server-only in `shared/trust.ts`
 * (and the computation lives in `server/teamPulse.ts` / `server/weekStatus.ts`).
 *
 * Tabs map a server-provided band to a label here; they never derive a band.
 */

/* --------------------------- Team Pulse bands --------------------------- */
export type CoverageBand = "good" | "partial" | "limited";
export type BlockerBand = "none" | "some" | "elevated";
export type StatusBand = "on_track" | "mixed" | "needs_attention";
export type ActivityTrend = "rising" | "stable" | "lower";

export const COVERAGE_LABEL: Record<CoverageBand, string> = {
  good: "Good coverage",
  partial: "Partial coverage",
  limited: "Limited coverage",
};
export const BLOCKER_LABEL: Record<BlockerBand, string> = {
  none: "None",
  some: "Some",
  elevated: "Elevated",
};
export const STATUS_LABEL: Record<StatusBand, string> = {
  on_track: "On track",
  mixed: "Mixed",
  needs_attention: "Needs attention",
};

/* ----------------------------- Day status ------------------------------ */
export type DayStatus =
  | "not_started"
  | "draft"
  | "ready"
  | "needs_update"
  | "update_available"
  | "approved"
  | "retracted"
  | "weekend_note"
  | "hidden";

export const DAY_STATUS_LABEL: Record<DayStatus, string> = {
  not_started: "Not started",
  draft: "Draft",
  ready: "Ready",
  // Approved, then the content was edited/re-drafted — there is an UNSHARED new
  // version; the team still sees the previous approved snapshot.
  needs_update: "Changes not shared",
  // Approved, and new activity was captured afterward, but the current content
  // still equals the approved snapshot (nothing re-drafted yet).
  update_available: "New updates captured",
  approved: "Approved",
  // Approved in history, but the owner UNSHARED it — the team no longer sees
  // it. Re-approval (through the preview) republishes.
  retracted: "Retracted",
  weekend_note: "Weekend note",
  hidden: "",
};

/** Arabic equivalents — same trust semantics, GCC business Arabic. */
export const DAY_STATUS_LABEL_AR: Record<DayStatus, string> = {
  not_started: "لم يبدأ",
  draft: "مسودة",
  ready: "جاهز",
  needs_update: "تغييرات غير مُشارَكة",
  update_available: "تحديثات جديدة مُلتقطة",
  approved: "معتمد",
  retracted: "مسحوب",
  weekend_note: "ملاحظة يوم راحة",
  hidden: "",
};

/** Locale-aware status label. English remains the default everywhere. */
export function dayStatusLabel(status: DayStatus, locale?: string): string {
  return locale === "ar"
    ? (DAY_STATUS_LABEL_AR[status] ?? "")
    : (DAY_STATUS_LABEL[status] ?? "");
}
